import type { RailBranch } from "../../data/rail-branches";
import { MS_PER_PIXEL } from "../timeline/book/BookLayout";
import { forkCurveSpan, joinCurveSpan, railLaneOffsetY } from "../timeline/rail/RailLanes/lanes";
import { timelineHomeMarkerCenterX } from "../timeline/scene/SceneMotion/motion";
import { pushThickPolyline } from "./thick-polyline";
import type { WorldView } from "./world-to-canvas";
import type { TimelineVertex } from "./vertex-primitives";

const RAIL_HALF_WIDTH_WORLD = 9;
/** World px between samples. Shorter than the stroke so the bend stays round. */
const CURVE_STEP_PX = 8;
const RAIL_R = 0.88;
const RAIL_G = 0.9;
const RAIL_B = 0.94;
const RAIL_A = 0.98;

interface Point {
  x: number;
  y: number;
}

export function appendRailBranches(
  verts: TimelineVertex[],
  branches: readonly RailBranch[],
  axisY: number,
  viewportWidth: number,
  view: WorldView,
): void {
  for (const branch of branches) {
    const points = branchPoints(branch, axisY, viewportWidth).map((point) => ({
      x: (point.x - view.originX) * view.zoom,
      y: (point.y - view.originY) * view.zoom,
    }));
    pushThickPolyline(
      verts,
      points,
      RAIL_HALF_WIDTH_WORLD * view.zoom,
      RAIL_R,
      RAIL_G,
      RAIL_B,
      RAIL_A,
    );
  }
}

function branchPoints(
  branch: RailBranch,
  axisY: number,
  viewportWidth: number,
): Point[] {
  const y = axisY + railLaneOffsetY(branch.lane, branch.laneOffsets);
  const fork = forkCurveSpan(branch);
  const join = joinCurveSpan(branch);
  const points: Point[] = [];
  if (branch.forkFromLane !== undefined) {
    const fromY = axisY + railLaneOffsetY(branch.forkFromLane, branch.laneOffsets);
    points.push(...curvePoints(
      msToX(fork.startMs, viewportWidth), fromY, msToX(fork.endMs, viewportWidth), y,
    ));
  } else {
    points.push({ x: msToX(branch.startMs, viewportWidth), y });
  }
  const straightEndMs = branch.joinToLane !== undefined ? join.startMs : branch.endMs;
  const straightEnd = msToX(straightEndMs, viewportWidth);
  const last = points[points.length - 1];
  if (last !== undefined && straightEnd > last.x + 0.5) {
    points.push({ x: straightEnd, y });
  }
  if (branch.joinToLane !== undefined) {
    const toY = axisY + railLaneOffsetY(branch.joinToLane, branch.laneOffsets);
    const joinStart = points[points.length - 1] ?? { x: straightEnd, y };
    points.push(...curvePoints(joinStart.x, y, msToX(join.endMs, viewportWidth), toY).slice(1));
  }
  return points;
}

function curvePoints(x0: number, y0: number, x1: number, y1: number): Point[] {
  const midX = (x0 + x1) / 2;
  const steps = curveSteps(x0, y0, x1, y1);
  const points: Point[] = [];
  for (let step = 0; step <= steps; step++) {
    points.push(cubic(
      { x: x0, y: y0 },
      { x: midX, y: y0 },
      { x: midX, y: y1 },
      { x: x1, y: y1 },
      step / steps,
    ));
  }
  return points;
}

function curveSteps(x0: number, y0: number, x1: number, y1: number): number {
  const span = Math.hypot(x1 - x0, y1 - y0);
  return Math.max(16, Math.ceil(span / CURVE_STEP_PX));
}

function cubic(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

function msToX(ms: number, viewportWidth: number): number {
  return timelineHomeMarkerCenterX(viewportWidth) + ms / MS_PER_PIXEL;
}
